import React, {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useSelector } from 'react-redux';

import { collection } from 'firebase/firestore';

import { FSCollections } from '@src/constants/database';
import { db } from '@src/firebase/firebase';
import { CommonReduxState } from '@src/models/commons/reduxState.interface';
import { IAddress } from '@src/models/interfaces/address.interface';
import { DEFAULT_ADDRESS_DISTANCE_METERS } from '@src/packages/addresses/constants';
import { useFirestoreQuery } from '@src/packages/utils';

export interface AddressFormState {
  id: string | null;
  organizationId: string | null;
  address: string;
  number: string;
  floorOffice: string;
  commune: string;
  city: string;
  region: string;
  country: string;
  zip: string;
  location: {
    distance: {
      meters: number;
      outside: boolean;
    };
    type: string;
    token: string | null;
    divId: string | null;
    position: unknown;
  };
}

interface AddressesContextValue {
  addressForm: AddressFormState;
  setAddressForm: Dispatch<SetStateAction<AddressFormState>>;
  addresses: IAddress[];
  addressesLoading: boolean;
  orgId: string;
}

export const AddressesContext = createContext<AddressesContextValue | null>(null);

const getInitialAddressForm = (organizationId: string | null = null): AddressFormState => ({
  id: null,
  organizationId,
  address: '',
  number: '',
  floorOffice: '',
  commune: '',
  city: '',
  region: '',
  country: '',
  zip: '',
  location: {
    distance: {
      meters: DEFAULT_ADDRESS_DISTANCE_METERS,
      outside: false,
    },
    type: 'GPS',
    token: null,
    divId: null,
    position: null,
  },
});

interface AddressesProviderProps {
  children: ReactNode;
}

export const AddressesProvider: React.FC<AddressesProviderProps> = ({ children }) => {
  const orgId = useSelector((state: CommonReduxState) => state.organizations.organization.id);
  const { ORGANIZATIONS, ADDRESSES } = FSCollections;

  const addressesQuery = collection(db, ORGANIZATIONS, orgId, ADDRESSES);
  const addressesResult = useFirestoreQuery(addressesQuery, [orgId]);
  const { data: addresses = [], loading: addressesLoading } = addressesResult;
  const [addressForm, setAddressForm] = useState<AddressFormState>(() =>
    getInitialAddressForm(orgId),
  );

  useEffect(() => {
    setAddressForm(getInitialAddressForm(orgId));
  }, [orgId]);

  const value = useMemo(
    () => ({
      addressForm,
      setAddressForm,
      addresses: addresses as IAddress[],
      addressesLoading,
      orgId,
    }),
    [addressForm, addresses, addressesLoading, orgId],
  );

  return <AddressesContext.Provider value={value}>{children}</AddressesContext.Provider>;
};

export const useAddressesContext = (): AddressesContextValue => {
  const context = useContext(AddressesContext);
  if (!context) {
    throw new Error('useAddressesContext must be used within an AddressesProvider');
  }
  return context;
};
